import create from "zustand";

export type TSelectedCompany = string;
export type TTheme = "light" | "dark";

const THEME_STORAGE_KEY = "theme";

const getInitialTheme = (): TTheme => {
  if (typeof window === "undefined") return "light";

  const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (savedTheme === "light" || savedTheme === "dark") return savedTheme;

  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }

  return "light";
};

interface SidebarState {
  sidebarOpen: boolean;
  sidebarMobileOpen: boolean;
  toggleSidebar: () => void;
  toggleMobileSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  selectedCompany: TSelectedCompany;
  setSelectedCompany: (company: TSelectedCompany) => void;
  theme: TTheme;
  setTheme: (theme: TTheme) => void;
  toggleTheme: () => void;
}

export const useGlobalStore = create<SidebarState>((set) => ({
  sidebarOpen: JSON.parse(localStorage.getItem("sidebar") || "true"),
  sidebarMobileOpen: false,
  toggleSidebar: () =>
    set((state) => {
      const newSidebarState = !state.sidebarOpen;
      localStorage.setItem("sidebar", JSON.stringify(newSidebarState));
      return { sidebarOpen: newSidebarState };
    }),
  toggleMobileSidebar: () =>
    set((state) => {
      return { sidebarMobileOpen: !state.sidebarMobileOpen };
    }),
  setSidebarOpen: (open: boolean) => {
    localStorage.setItem("sidebar", JSON.stringify(open));
    set({ sidebarOpen: open });
  },
  theme: getInitialTheme(),
  setTheme: (theme: TTheme) => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    set({ theme });
  },
  toggleTheme: () =>
    set((state) => {
      const nextTheme: TTheme = state.theme === "light" ? "dark" : "light";
      localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
      return { theme: nextTheme };
    }),
  selectedCompany: (() => {
    const fromEnv = process.env.REACT_APP_COMPANY;
    if (fromEnv) return fromEnv as TSelectedCompany;
    return (localStorage.getItem("selectedCompany") as TSelectedCompany) || "tteld";
  })(),
  setSelectedCompany: (company: TSelectedCompany) => {
    set(() => {
      if (!process.env.REACT_APP_COMPANY) {
        localStorage.setItem("selectedCompany", company);
      }
      return { selectedCompany: company };
    });
  },
}));
